const PROD_MARKETPLACE_ORIGIN = process.env.REACT_APP_MARKETPLACE_ORIGIN || 'https://marketplace.farmerkonect.com'
const TEST_MARKETPLACE_ORIGIN = process.env.REACT_APP_MARKETPLACE_ORIGIN_TEST || 'https://test-marketplace.farmerkonect.com'

/** True when admin app runs on localhost or a test environment */
export const isTestMarketplaceEnvironment = () => {
  const apiBase = (process.env.REACT_APP_BASE_URL || '').toLowerCase()
  if (apiBase.includes('test') || apiBase.includes('localhost')) return true

  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase()
    if (host === 'localhost' || host === '127.0.0.1' || host.includes('test')) {
      return true
    }
  }

  return false
}

export const getMarketplaceOrigin = () =>
  isTestMarketplaceEnvironment() ? TEST_MARKETPLACE_ORIGIN : PROD_MARKETPLACE_ORIGIN

export const getMarketplaceProductBaseUrl = () => `${getMarketplaceOrigin()}/marketplace`

export const getMarketplaceHomeUrl = () => `${getMarketplaceOrigin()}/`

/**
 * Resolve storefront identifier — provider user id only (from API or owner/id fallback).
 */
export const resolveStorefrontIdentifier = (userData) => {
  if (!userData) return null

  if (userData.storefront_identifier != null && userData.storefront_identifier !== '') {
    return String(userData.storefront_identifier)
  }

  const providerId = userData.owner_id || userData.managed_by || userData.id
  return providerId ? String(providerId) : null
}

export const canShareStorefront = (userData) => {
  if (!userData) return false
  const isServiceProvider = userData.user_type === 'service_provider'
  const isManagedTeam = Boolean(userData.is_managed || userData.is_managed_user)
  return (isServiceProvider || isManagedTeam) && Boolean(resolveStorefrontIdentifier(userData))
}

/** Public storefront page on the marketplace app */
export const getStorefrontUrl = (storefrontIdentifier) => {
  if (!storefrontIdentifier) return ''
  return `${getMarketplaceOrigin()}/store/${storefrontIdentifier}`
}

/** Provider catalog API (marketplace app — no auth) */
export const getProviderCatalogApiPath = (identifier, query = '') => {
  const suffix = query ? (query.startsWith('?') ? query : `?${query}`) : ''
  return `service-orders/products/provider/${identifier}/${suffix}`
}

/** Products and services share the same marketplace listing URL shape */
export const getMarketplaceItemId = (item) => item?.id || item?.product_id || null

export const getMarketplaceProductUrl = (itemOrId) => {
  const id = typeof itemOrId === 'object' ? getMarketplaceItemId(itemOrId) : itemOrId
  if (!id) return ''
  return `${getMarketplaceProductBaseUrl()}/${id}`
}

export const getMarketplaceItemUrl = getMarketplaceProductUrl

export const shareMarketplaceItemLink = async (
  item,
  { onCopied, onError } = {}
) => {
  const url = getMarketplaceItemUrl(item)
  if (!url) {
    onError?.('Marketplace link is unavailable')
    return false
  }

  const label =
    item?.item_type === 'service'
      ? 'Marketplace service'
      : 'Marketplace product'

  try {
    if (navigator.share) {
      await navigator.share({
        title: item?.name || item?.product_name || label,
        url,
      })
      return true
    }

    await navigator.clipboard.writeText(url)
    onCopied?.(url)
    return true
  } catch (error) {
    if (error?.name === 'AbortError') return false
    onError?.('Failed to share link')
    return false
  }
}

/** @deprecated use shareMarketplaceItemLink */
export const shareMarketplaceProductLink = shareMarketplaceItemLink

export const shareStorefrontLink = async (
  storefrontIdentifier,
  { title, onError } = {}
) => {
  const url = getStorefrontUrl(storefrontIdentifier)
  if (!url) {
    onError?.('Store link is unavailable. Complete your business profile to get a storefront.')
    return false
  }

  if (!navigator.share) {
    onError?.('Sharing is not supported on this device. Use Copy link instead.')
    return false
  }

  try {
    await navigator.share({
      title: title || 'My marketplace store',
      url,
    })
    return true
  } catch (error) {
    if (error?.name === 'AbortError') return false
    onError?.('Failed to share store link')
    return false
  }
}

export const copyStorefrontLink = async (
  storefrontIdentifier,
  { onCopied, onError } = {}
) => {
  const url = getStorefrontUrl(storefrontIdentifier)
  if (!url) {
    onError?.('Store link is unavailable. Complete your business profile to get a storefront.')
    return false
  }

  try {
    await navigator.clipboard.writeText(url)
    onCopied?.(url)
    return true
  } catch (error) {
    onError?.('Failed to copy store link')
    return false
  }
}
